import { useStripePlans } from '@hooks/api/stripe';

import type { Plan } from '@models/stripe';

import type { StripeCoupon, PlanType } from 'generated/types';

export type CostCalculatorCosts = {
  subscriptionCosts: Record<PlanType, number>;
  subscriptionCost: number;
  oneTimeCost: number;
  subtotal: number;
  total: number;
  promo: number;
  taxes: number;
  numPlans: Record<PlanType, number>;
  nextPayment: number;
  planSavings: number;
  planSavingsPercent: number;
  currency: 'cad' | 'usd';
  plans: Record<PlanType, Plan | null>;
  loading: boolean;
  coupon: StripeCoupon | null;
  billingPeriod: 'month' | 'year';
  taxRate: number;
};

export function useCostCalculator({
  taxRate = 0,
  coupon = null,
  members = [],
  currency = 'cad',
  billingPeriod = 'month',
}: {
  taxRate?: number;
  coupon?: StripeCoupon | null;
  members?: { pricing?: PlanType | null }[];
  currency?: 'cad' | 'usd';
  billingPeriod?: 'month' | 'year';
}): CostCalculatorCosts {
  const { data: plans, loading } = useStripePlans();

  function formatCurrency(x: number): number {
    return Number(x.toFixed(2));
  }

  function countPlans(): Record<PlanType, number> {
    return members
      .flatMap((member) => (member.pricing ? [member.pricing] : []))
      .reduce((acc, curr) => ({ ...acc, [curr]: acc[curr] + 1 }), {
        PATIENT: 0,
      });
  }

  function calculateCostPerSubscription(): Record<PlanType, number> {
    const planCount = countPlans();

    return Object.keys(planCount).reduce<Record<PlanType, number>>(
      (acc, curr) => {
        const planType = curr as PlanType;
        const plan = plans[planType];

        if (!plan) {
          return acc;
        }

        return {
          ...acc,
          [planType]:
            plan.getSubscriptionCost(billingPeriod, currency) *
            planCount[planType],
        };
      },
      {
        PATIENT: 0,
      }
    );
  }

  function calculateSubscriptionCost(): number {
    const costs = calculateCostPerSubscription();
    return Object.values(costs).reduce((acc, curr) => acc + curr, 0);
  }

  function calculateOneTimeCost(): number {
    return members.reduce((acc, curr) => {
      if (!curr.pricing) {
        return acc;
      }
      const plan = plans[curr.pricing];
      if (!plan) {
        return acc;
      }
      return acc + plan.getOneTimeCost(billingPeriod, currency);
    }, 0);
  }

  function calculatePromo(): number {
    if (!coupon) return 0.0;

    let cost = 0.0;
    if (['forever', 'repeating'].includes(coupon.duration)) {
      cost = calculateOneTimeCost() + calculateSubscriptionCost();
    } else if (coupon.duration === 'once') {
      cost = calculateOneTimeCost();
    }

    if (coupon.percent_off) return -1 * ((cost * coupon.percent_off) / 100);
    if (coupon.amount_off) return (-1 * coupon.amount_off) / 100;

    return cost;
  }

  function calculateSubTotal(): number {
    const subtotal =
      calculateOneTimeCost() + calculatePromo() + calculateSubscriptionCost();
    return subtotal > 0 ? subtotal : 0.0;
  }

  function calculateTaxes(): number {
    return calculateSubTotal() * taxRate;
  }

  function calculateTotal(): number {
    return calculateSubTotal() + calculateTaxes();
  }

  function calculateNextPayment(): number {
    const subCost = calculateSubscriptionCost();
    if (coupon && coupon.duration === 'forever') {
      return subCost - (subCost * (coupon.percent_off ?? 0)) / 100;
    }
    return subCost;
  }

  function calculatePlanSavingsPercent(): number {
    let monthlyCost = 0.0;
    let annualCost = 0.0;

    members.forEach((m) => {
      if (m.pricing) {
        const plan = plans[m.pricing];
        if (plan) {
          monthlyCost += plan.getSubscriptionCost('month', currency);
          annualCost += plan.getSubscriptionCost('year', currency);
        }
      }
    });
    return Math.round((1 - annualCost / (monthlyCost * 12)) * 100);
  }

  function calculatePlanSavings(): number {
    return (calculatePlanSavingsPercent() / 100) * calculateSubscriptionCost();
  }

  return {
    subscriptionCosts: calculateCostPerSubscription(),
    subscriptionCost: formatCurrency(calculateSubscriptionCost()),
    oneTimeCost: formatCurrency(calculateOneTimeCost()),
    subtotal: formatCurrency(calculateSubTotal()),
    total: formatCurrency(calculateTotal()),
    promo: formatCurrency(calculatePromo()),
    taxes: formatCurrency(calculateTaxes()),
    numPlans: countPlans(),
    nextPayment: formatCurrency(calculateNextPayment()),
    planSavings: formatCurrency(calculatePlanSavings()),
    planSavingsPercent: calculatePlanSavingsPercent(),
    currency,
    plans,
    loading,
    coupon,
    billingPeriod,
    taxRate,
  };
}
